import { useCallback, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import { PlaybackSpeed, PLAYBACK_INTERVAL_BY_SPEED } from '../constants/journey';
import { JourneyPoint } from '~/utils/interface/mapback';

const usePlaybackAnimation = (
  allPoints: JourneyPoint[],
  isPlaying: boolean,
  currentPointIndex: number,
  playSpeed: PlaybackSpeed,
  onPointChange: (index: number) => void,
  onProgressChange?: (progressIndex: number) => void
): void => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearPlaybackTimer = useCallback(() => {
    if (!timeoutRef.current) return;
    clearTimeout(timeoutRef.current);
    timeoutRef.current = null;
  }, []);

  const safePointChange = useCallback(
    (index: number) => {
      if (allPoints.length <= 0) {
        onPointChange(0);
        return;
      }

      const maxIndex = Math.max(0, allPoints.length - 1);
      const clampedIndex = Math.min(Math.max(index, 0), maxIndex);
      onPointChange(clampedIndex);
    },
    [allPoints.length, onPointChange]
  );

  useEffect(() => {
    clearPlaybackTimer();

    if (!isPlaying || allPoints.length <= 0)
      return () => {
        clearPlaybackTimer();
      };

    if (currentPointIndex < 0 || currentPointIndex >= allPoints.length) {
      safePointChange(0);
      return clearPlaybackTimer;
    }

    const waitMs = PLAYBACK_INTERVAL_BY_SPEED[playSpeed];
    const startedAt = dayjs().valueOf();
    const nextIndex = currentPointIndex + 1;

    timeoutRef.current = setTimeout(() => {
      const elapsedMs = dayjs().diff(dayjs(startedAt), 'millisecond');

      if (elapsedMs < 0) {
        safePointChange(currentPointIndex);
        return;
      }

      // Reach the end: reset to first point (auto replay behavior).
      if (nextIndex >= allPoints.length) {
        if (onProgressChange) onProgressChange(0);
        safePointChange(0);
        return;
      }

      if (onProgressChange) onProgressChange(nextIndex);
      safePointChange(nextIndex);
    }, waitMs);

    return () => {
      clearPlaybackTimer();
    };
  }, [
    allPoints,
    isPlaying,
    currentPointIndex,
    playSpeed,
    clearPlaybackTimer,
    safePointChange,
    onProgressChange,
  ]);
};

export default usePlaybackAnimation;
